/**
 * Découper une photo à la zone retenue, au web : la toile du navigateur, comme `ImageCropModal.web.js`
 * (`createCroppedImage`) — un JPEG à 0,95, servi par une adresse `blob:`, nommé `cropped_<instant>.jpg`.
 * La photo se lit par son adresse (`blob:`, `data:` ou une adresse du serveur), jamais par son type. Une
 * adresse du serveur (une pièce de la conversation) se relit d'abord en CORS, sans cache : l'image que la page
 * affiche déjà a pu être servie sans en-tête CORS et mise en cache ainsi, et la toile en serait alors
 * « souillée » (`toBlob` refusé) — la découpe échouait en silence.
 *
 * Rend `null` quand la découpe échoue (une photo illisible, une toile vide) : l'appelant envoie alors la
 * photo entière, ce que faisait l'ancien écran. Rend aussi de quoi libérer l'adresse du JPEG (`dispose`).
 */
import { cropPixels } from "../formatters/cropAreaFormatter"
import { type UIActivityFile } from "../types/activityGeneration"
import { type UITutorCropArea } from "../types/tutorEleves"
import { drawJpeg, loadImage } from "./canvasJpeg.web"

const JPEG_QUALITY = 0.95

/** L'adresse que la toile peut lire : celle du fichier, ou celle d'une copie relue en CORS. */
async function readableUri(uri: string): Promise<{ uri: string; release: () => void }> {
    if (uri.startsWith("blob:") || uri.startsWith("data:")) return { uri, release: () => {} }
    const response = await fetch(uri, { mode: "cors", cache: "no-store" })
    if (!response.ok) throw new Error(`photo illisible (${response.status})`)
    const local = URL.createObjectURL(await response.blob())
    return { uri: local, release: () => URL.revokeObjectURL(local) }
}

export async function cropImageFile(
    file: UIActivityFile,
    area: UITutorCropArea
): Promise<{ file: UIActivityFile; dispose: () => void } | null> {
    let release = () => {}
    try {
        const readable = await readableUri(file.uri)
        release = readable.release
        const image = await loadImage(readable.uri, { crossOrigin: true })
        const pixels = cropPixels(area, image.naturalWidth, image.naturalHeight)
        const { uri, dispose } = await drawJpeg(image, pixels, pixels, JPEG_QUALITY)
        return { file: { uri, name: `cropped_${Date.now()}.jpg`, mimeType: "image/jpeg" }, dispose }
    } catch {
        return null
    } finally {
        release()
    }
}
